import { passenger } from "../../../objects/v1_2/Booking/Passenger";
import { booking_search_group } from "../../../objects/v1_2/Booking_Details/booking_search_group";
import { country_nationality } from "../../../objects/v1_3/web_engine/country_nationality";

// Single source of truth for which passenger fields are shown/required and whether they are valid.
// Pure functions only: used by the passenger form (inline errors) and by checkout (gating the save).

export type Passenger_Field =
    | 'first_name'
    | 'family_name'
    | 'date_of_birth'
    | 'gender'
    | 'iso_nationality'
    | 'post_code'
    | 'email'
    | 'confirm_email'
    | 'telephone_number'
    | 'emergency_contact'
    | 'emergency_number';

// Display order; also the order used to focus the first invalid field.
export const Passenger_Fields: Passenger_Field[] = [
    'first_name', 'family_name', 'date_of_birth', 'gender', 'iso_nationality', 'post_code',
    'email', 'confirm_email', 'telephone_number', 'emergency_contact', 'emergency_number'
];

export type Passenger_Errors = Partial<Record<Passenger_Field, string>>;

// Form input that is not stored on the passenger itself (partial dates, dialling codes, confirm email).
// Kept in the booking store per passenger so it survives navigating away from checkout and back.
export interface Passenger_Draft {
    confirm_email: string;
    phone_code: string;
    phone_number: string;
    emergency_code: string;
    emergency_number: string;
    dob_day: string;
    dob_month: string;
    dob_year: string;
    // Placeholder names the booking was created with (consumer only), so they can be cleared again after a refresh.
    placeholder_first_name: string;
    placeholder_family_name: string;
}

export interface Passenger_Rules_Context {
    hide_date_of_birth: boolean;
    earliest_departure: Date;
    latest_departure: Date;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHILD_MIN_AGE = 5;
const CHILD_MAX_AGE = 16;
const ADULT_MIN_AGE = 17;

const REQUIRED_MESSAGES: Record<Passenger_Field, string> = {
    first_name: "Enter a first name",
    family_name: "Enter a surname",
    date_of_birth: "Enter a date of birth",
    gender: "Select a gender",
    iso_nationality: "Select a nationality",
    post_code: "Enter a postcode",
    email: "Enter an email address",
    confirm_email: "Confirm the email address",
    telephone_number: "Enter a mobile phone number",
    emergency_contact: "Enter an emergency contact name",
    emergency_number: "Enter an emergency contact phone number"
};

export function Empty_Draft(): Passenger_Draft {
    return {
        confirm_email: "",
        phone_code: "",
        phone_number: "",
        emergency_code: "",
        emergency_number: "",
        dob_day: "",
        dob_month: "",
        dob_year: "",
        placeholder_first_name: "",
        placeholder_family_name: ""
    };
}

// Seeds a draft from what is already stored on the passenger (e.g. returning to checkout).
export function Draft_From_Passenger(p: passenger): Passenger_Draft {
    const draft = Empty_Draft();
    draft.confirm_email = p.email ?? "";
    draft.phone_number = p.telephone_number ?? "";
    draft.emergency_number = p.emergency_number ?? "";

    const dob = Parse_Date_Of_Birth(p.date_of_birth);
    if (dob) {
        draft.dob_day = dob.day;
        draft.dob_month = dob.month;
        draft.dob_year = dob.year;
    }
    return draft;
}

export function Is_Child(p: passenger): boolean {
    return (p.age_category ?? "").toLowerCase() === 'child';
}

function Listed(p: passenger, field: string): 'required' | 'optional' | null {
    if (p.required_fields?.includes(field)) {
        return 'required';
    }
    if (p.optional_fields?.includes(field)) {
        return 'optional';
    }
    return null;
}

export function Is_Visible(p: passenger, field: Passenger_Field, ctx: Passenger_Rules_Context): boolean {
    switch (field) {
    case 'date_of_birth':
        return !ctx.hide_date_of_birth && (Listed(p, field) !== null || Is_Child(p));
    case 'confirm_email':
        return Listed(p, 'email') === 'required';
    default:
        return Listed(p, field) !== null;
    }
}

export function Is_Required(p: passenger, field: Passenger_Field, ctx: Passenger_Rules_Context): boolean {
    switch (field) {
    case 'date_of_birth':
        return !ctx.hide_date_of_birth && (Listed(p, field) === 'required' || Is_Child(p));
    case 'confirm_email':
        return Listed(p, 'email') === 'required';
    default:
        return Listed(p, field) === 'required';
    }
}

// Accepts the DD-MM-YYYY format this form writes and the YYYY-MM-DD format the API may return.
export function Parse_Date_Of_Birth(value: string | null | undefined): { day: string, month: string, year: string } | null {
    const text = (value ?? "").trim();
    let match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(text);
    if (match) {
        return { day: match[1].padStart(2, '0'), month: match[2].padStart(2, '0'), year: match[3] };
    }
    match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
    if (match) {
        return { day: match[3].padStart(2, '0'), month: match[2].padStart(2, '0'), year: match[1] };
    }
    return null;
}

// Returns the date if the three parts make a real calendar date between 1900 and today.
export function Draft_Date_Of_Birth(draft: Passenger_Draft): Date | null {
    if (!/^\d{1,2}$/.test(draft.dob_day) || !/^\d{1,2}$/.test(draft.dob_month) || !/^\d{4}$/.test(draft.dob_year)) {
        return null;
    }
    const day = Number(draft.dob_day);
    const month = Number(draft.dob_month);
    const year = Number(draft.dob_year);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    if (year < 1900 || date > new Date()) {
        return null;
    }
    return date;
}

// The value stored on the passenger: DD-MM-YYYY, or null while incomplete/invalid.
export function Format_Date_Of_Birth(draft: Passenger_Draft): string | null {
    if (!Draft_Date_Of_Birth(draft)) {
        return null;
    }
    return `${draft.dob_day.padStart(2, '0')}-${draft.dob_month.padStart(2, '0')}-${draft.dob_year}`;
}

// Digits only, one leading trunk zero dropped (07700 900123 -> 7700900123).
export function Clean_Phone_Number(number: string): string {
    return (number ?? "").replace(/\D/g, '').replace(/^0/, '');
}

// The value stored on the passenger: dialling code + number, or null when no number was entered.
export function Format_Phone(code: string, number: string): string | null {
    const digits = Clean_Phone_Number(number);
    if (digits === "") {
        return null;
    }
    return (code ?? "") + digits;
}

// Splits a stored number back into dialling code + local number using the longest matching code.
export function Split_Phone(value: string, countries: country_nationality[]): { code: string, number: string } | null {
    let best = "";
    countries.forEach(c => {
        const code = c.dialing_code ?? "";
        if (/^\d+$/.test(code) && value.startsWith(code) && code.length > best.length) {
            best = code;
        }
    });
    return best === "" ? null : { code: best, number: value.substring(best.length) };
}

export function Age_On(dob: Date, date: Date): number {
    let age = date.getFullYear() - dob.getFullYear();
    const m = date.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && date.getDate() < dob.getDate())) {
        age--;
    }
    return age;
}

function Is_Blank(value: string | null | undefined): boolean {
    return value == null || String(value).trim() === "";
}

function Phone_Error(p: passenger, field: 'telephone_number' | 'emergency_number', code: string, number: string, ctx: Passenger_Rules_Context): string | null {
    const digits = Clean_Phone_Number(number);
    if (digits === "") {
        return Is_Required(p, field, ctx) ? REQUIRED_MESSAGES[field] : null;
    }
    if (Is_Blank(code)) {
        return "Select a country code";
    }
    if (digits.length < 6 || (code + digits).length > 15) {
        return "Enter a valid phone number";
    }
    return null;
}

function Date_Of_Birth_Error(p: passenger, draft: Passenger_Draft, ctx: Passenger_Rules_Context): string | null {
    const entered = !Is_Blank(draft.dob_day) || !Is_Blank(draft.dob_month) || !Is_Blank(draft.dob_year);
    if (!entered) {
        return Is_Required(p, 'date_of_birth', ctx) ? REQUIRED_MESSAGES.date_of_birth : null;
    }
    const dob = Draft_Date_Of_Birth(draft);
    if (!dob) {
        return "Enter a real date of birth, for example 27 03 1985";
    }
    if (Is_Child(p)) {
        if (Age_On(dob, ctx.earliest_departure) < CHILD_MIN_AGE) {
            return `Children must be at least ${CHILD_MIN_AGE} years old on the departure date`;
        }
        if (Age_On(dob, ctx.latest_departure) > CHILD_MAX_AGE) {
            return `Children must be ${CHILD_MAX_AGE} or under on the departure date`;
        }
    } else if (Age_On(dob, ctx.earliest_departure) < ADULT_MIN_AGE) {
        return `Adults must be ${ADULT_MIN_AGE} or over on the departure date`;
    }
    return null;
}

export function Validate_Passenger(p: passenger, draft: Passenger_Draft, ctx: Passenger_Rules_Context): Passenger_Errors {
    const errors: Passenger_Errors = {};
    const need = (field: Passenger_Field) => Is_Visible(p, field, ctx) && Is_Required(p, field, ctx);

    (['first_name', 'family_name', 'gender', 'iso_nationality', 'post_code', 'emergency_contact'] as Passenger_Field[]).forEach(field => {
        if (need(field) && Is_Blank((p as any)[field])) {
            errors[field] = REQUIRED_MESSAGES[field];
        }
    });

    if (Is_Visible(p, 'email', ctx)) {
        const email = (p.email ?? "").trim();
        if (email === "") {
            if (need('email')) {
                errors.email = REQUIRED_MESSAGES.email;
            }
        } else if (!EMAIL_PATTERN.test(email)) {
            errors.email = "Enter an email address in the correct format, like name@example.com";
        }
    }

    if (Is_Visible(p, 'confirm_email', ctx) && !errors.email) {
        const confirm = draft.confirm_email.trim();
        if (confirm === "") {
            errors.confirm_email = REQUIRED_MESSAGES.confirm_email;
        } else if (confirm.toLowerCase() !== (p.email ?? "").trim().toLowerCase()) {
            errors.confirm_email = "Email addresses do not match";
        }
    }

    if (Is_Visible(p, 'telephone_number', ctx)) {
        const error = Phone_Error(p, 'telephone_number', draft.phone_code, draft.phone_number, ctx);
        if (error) {
            errors.telephone_number = error;
        }
    }

    if (Is_Visible(p, 'emergency_number', ctx)) {
        const error = Phone_Error(p, 'emergency_number', draft.emergency_code, draft.emergency_number, ctx);
        if (error) {
            errors.emergency_number = error;
        }
    }

    if (Is_Visible(p, 'date_of_birth', ctx)) {
        const error = Date_Of_Birth_Error(p, draft, ctx);
        if (error) {
            errors.date_of_birth = error;
        }
    }

    return errors;
}

export function Rules_Context(booking: booking_search_group | null | undefined): Passenger_Rules_Context {
    const dates = ((booking as any)?.trips ?? [])
        .map((trip: any) => trip?.departure_date ? new Date(trip.departure_date) : null)
        .filter((d: Date | null) => d !== null && !isNaN(d.getTime())) as Date[];
    const now = new Date();

    return {
        hide_date_of_birth: import.meta.env.VITE_DEFAULT_PARENT == "AAT",
        earliest_departure: dates.length ? new Date(Math.min(...dates.map(d => d.getTime()))) : now,
        latest_departure: dates.length ? new Date(Math.max(...dates.map(d => d.getTime()))) : now
    };
}

// The booking model types passengers as 'client', but at runtime they carry required_fields/optional_fields.
export function Booking_Passengers(booking: booking_search_group | null | undefined): passenger[] {
    return ((booking?.passengers ?? []) as unknown) as passenger[];
}

// Index of the first passenger with errors, or -1 when everyone is complete.
export function First_Invalid_Passenger(booking: booking_search_group, drafts: Record<string, Passenger_Draft>, ctx: Passenger_Rules_Context): number {
    return Booking_Passengers(booking).findIndex((p: passenger) => {
        const draft = drafts[p.passenger_identifier ?? ""] ?? Draft_From_Passenger(p);
        return Object.keys(Validate_Passenger(p, draft, ctx)).length > 0;
    });
}

// Tidies values just before they are saved (same shape the API received before: names as strings, max 30 chars).
export function Normalise_Passenger(p: passenger): void {
    p.first_name = (p.first_name ?? "").trim().substring(0, 30);
    p.family_name = (p.family_name ?? "").trim().substring(0, 30);
    p.emergency_contact = (p.emergency_contact ?? "").trim().substring(0, 30);
    p.email = p.email == null ? null : (p.email.trim() || null);
    p.post_code = p.post_code == null ? null : p.post_code.trim();
}
